import { type AxiosError } from 'axios'
import {
  type QueryFunctionContext,
  type QueryObserverOptions,
  useQuery
} from 'react-query'

import { fetchQueryData } from 'hooks/use-gremlin-query'
import { oauthQueryKeys } from 'queries/oauth/query-keys'

import type { OAuthConsentDetails } from 'types/api/oauth/consent'

async function fetchOAuthConsentDetails({
  queryKey: [endpoint]
}: QueryFunctionContext<ReturnType<typeof oauthQueryKeys.consentDetails>>) {
  return await fetchQueryData<OAuthConsentDetails>(endpoint, {
    // Required, for the same reason the confirm POST needs it. The API binds both consent
    // endpoints to the browser that began the authorization, using an HttpOnly
    // `oauth2_authorize_state` cookie set on the API origin by GET /oauth2/authorize.
    //
    // This app is same-site with the API but cross-origin from it, and a browser attaches no
    // cookies to a cross-origin XHR unless the request is credentialed -- so without this the
    // cookie never arrives, the API answers 400, and the consent screen can never load.
    withCredentials: true
  })
}

/**
 * Loads what the consent screen needs to render.
 *
 * Deliberately not retried. The parked authorization request is single-use and short-lived, so a
 * 400 here means expired, already consumed, or never existed -- all states that retrying cannot
 * improve, and retrying a request the user is waiting on just delays the error they need to see.
 */
export function useGetOAuthConsentDetails(
  requestId: string,
  queryOptions?: Omit<
    QueryObserverOptions<OAuthConsentDetails, AxiosError>,
    'queryFn' | 'queryKey'
  >
) {
  return useQuery<OAuthConsentDetails, AxiosError>({
    retry: false,
    ...queryOptions,
    queryFn: fetchOAuthConsentDetails,
    queryKey: oauthQueryKeys.consentDetails(requestId)
  })
}
